import React, { useEffect, useState, useCallback } from "react";
import "../styles/SubNavigationBar.css";
import "../styles/AppLayout.css"; // to use .reapplied-notice-global style
import { useNavigate, useParams } from "react-router-dom";
import useAccess from "../hooks/useAccess";
import axios from "axios";

const SubNavigationBar = ({ tab, sidebarOpen }) => {
  const navigate = useNavigate();
  const { id: paramId } = useParams();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Determine if admin and which user is being viewed
  const loggedInId = localStorage.getItem("userId");
  const userRole = localStorage.getItem("userRole");
  const isAdmin = userRole === "1";
  const selectedUserId = isAdmin && paramId ? paramId : loggedInId;

  // State
  const [categories, setCategories] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);

  //  Access Control
  const { hasPermission } = useAccess();

  // ⭐ NEW: Centralized PERMISSION Map
  const PERMISSIONS = {
    drivers: {
      view: hasPermission("AdminDashboard", "Driver License", "view"),
    },
    healthcards: {
      view: hasPermission("AdminDashboard", "Health Card", "view"),
    },
    passports: {
      view: hasPermission("AdminDashboard", "Passport", "view"),
    },
    fastcards: {
      view: hasPermission("AdminDashboard", "Fast Card", "view"),
    },
    prcards: {
      view: hasPermission("AdminDashboard", "PR Card", "view"),
    },
    workpermits: {
      view: hasPermission("AdminDashboard", "Work Permit", "view"),
    },
    workhistory: {
      view: hasPermission("AdminDashboard", "Work History", "view"),
    },
    emergency: {
      view: hasPermission("AdminDashboard", "Emergency", "view"),
    },
    vacation: {
      view: hasPermission("AdminDashboard", "Vacation", "view"),
    },
    directdeposit: {
      view: hasPermission("AdminDashboard", "Direct Deposit", "view"),
    },
    fuelcards: {
      view: hasPermission("AdminDashboard", "Fuel Card", "view"),
    },
    account_setting: {
      view: hasPermission("AccountSetting", "Account Setting", "view"),
    },
    permissions: {
      view: hasPermission("PermissionManagerPage", "Permissions", "view"),
    },
    users: {
      view: hasPermission("AdminUser", "User", "view"),
    },
  };

  // Fetch dropdowns (categories, statuses)
  const fetchLookups = useCallback(async () => {
    try {
      setLoading(true);
      const headers = {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };
      const [catRes, statusRes] = await Promise.all([
        axios.get(`${BASE_URL}/api/dropdowns/categories`, { headers }),
        axios.get(`${BASE_URL}/api/dropdowns/user-status`, { headers }),
      ]);
      setCategories(catRes.data || []);
      setStatuses(statusRes.data || []);
    } catch (e) {
      console.error("Failed to fetch nav lookups", e);
    } finally {
      setLoading(false);
    }
  }, [BASE_URL]);

  useEffect(() => {
    fetchLookups();
  }, [fetchLookups]);

  // 🧩 Fetch the selected user's profile (for re_applied flag)
  useEffect(() => {
    const fetchSelectedUser = async () => {
      if (!selectedUserId) return;
      try {
        const res = await axios.get(`${BASE_URL}/api/users/${selectedUserId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setSelectedUser(res.data);
      } catch (err) {
        console.error("Error fetching selected user:", err);
      }
    };

    fetchSelectedUser();
  }, [selectedUserId, BASE_URL]);

  // Navigation helpers
  const toUsers = (params = {}) => {
    const sp = new URLSearchParams({ tab: "users", ...params });
    navigate(`/admin?${sp.toString()}`);
  };

  const handleNavigateToUserAll = () => {
    const associate = statuses.find(
      (s) => (s.s_TranslatedText || "").trim() === "Associate"
    );
    if (associate) toUsers({ status: "Associate" });
    else toUsers();
  };

  const handleNavigateToAdd = () => navigate("/form");
  const toCategory = (c_id) => toUsers({ filter: getCatNameById(c_id) });

  const byName = (name) =>
    categories.find((c) => (c.c_name || "").trim() === name);

  const DRIVER_ITEMS = [
    { name: "Driver", label: "Company Driver" },
    { name: "Owner Operator", label: "Owner/Operator" },
    { name: "Owner Operator Driver", label: "Owner/Operator Driver" },
    { name: "Independent Contractor", label: "Independent Contractor" },
    { name: "Agency Driver", label: "Agency Driver" },
    { name: "Agent", label: "Agent" },
  ];

  const NON_DRIVER_ITEMS = [
    { name: "Office Staff", label: "Office Staff" },
    { name: "Shop", label: "Shop" },
    { name: "Non Driving Owner Operator", label: "Non Driving Owner/Operator" },
  ];

  const getCatNameById = (c_id) => {
    const match = categories.find((c) => String(c.c_id) === String(c_id));
    return match?.c_name || "";
  };

  return (
    <div className={`subnav-container ${sidebarOpen ? "shifted" : ""}`}>
      {/* Flex container to separate menu and banner */}
      <div
        className="dashboard-menu-bar"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between", // ⬅ pushes menu left & banner right
          flexWrap: "nowrap",
        }}
      >
        {/* LEFT: Menu Sections */}
        <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
          {/* Recruitment */}
          <div className="menu-item admin-item">
            <div className="admin-toggle">Recruitment ⏷</div>
            <div className="admin-menu">
              <div
                className="admin-item"
                onClick={() => navigate("/job-descriptions")}
              >
                Descriptions
              </div>
              <div
                className="admin-item"
                onClick={() => navigate("/positions")}
              >
                Positions
              </div>
            </div>
          </div>

          {/* Associate List */}
          <div className="menu-item admin-item">
            <div className="admin-toggle">Associate List ⏷</div>
            <div className="admin-menu">
              <div className="admin-item" onClick={handleNavigateToUserAll}>
                <strong>All Associates</strong>
              </div>
              <div className="admin-item" onClick={handleNavigateToAdd}>
                <strong>Add New Associate</strong>
              </div>

              <div className="admin-item">
                <strong>Drivers</strong>
              </div>
              <hr className="dropdown-divider" />

              {loading && <div className="admin-item text-muted">Loading…</div>}
              {!loading &&
                DRIVER_ITEMS.map(({ name, label }) => {
                  const cat = byName(name);
                  if (!cat) return null;
                  return (
                    <div
                      key={cat.c_id}
                      className="admin-item"
                      onClick={() => toCategory(cat.c_id)}
                    >
                      {label}
                    </div>
                  );
                })}

              <div className="admin-item">
                <strong>Non-Drivers</strong>
              </div>
              <hr className="dropdown-divider" />

              {!loading &&
                NON_DRIVER_ITEMS.map(({ name, label }) => {
                  const cat = byName(name);
                  if (!cat) return null;
                  return (
                    <div
                      key={cat.c_id}
                      className="admin-item"
                      onClick={() => toCategory(cat.c_id)}
                    >
                      {label}
                    </div>
                  );
                })}
            </div>
          </div>

          {/* Admin Tools */}
          <div className="menu-item admin-item">
            <div className="admin-toggle">Admin Tools ⏷</div>
            <div className="admin-menu">
              {PERMISSIONS.drivers.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=drivers")}
                >
                  🚗 Driver IDs
                </div>
              )}
              {PERMISSIONS.healthcards.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=healthcards")}
                >
                  💳 Health Cards
                </div>
              )}
              {PERMISSIONS.fastcards.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=fastcards")}
                >
                  ⚡️ Fast Cards
                </div>
              )}
              {PERMISSIONS.passports.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=passports")}
                >
                  🛂 Passports
                </div>
              )}
              {PERMISSIONS.prcards.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=prcards")}
                >
                  🪪 PR Cards
                </div>
              )}
              {PERMISSIONS.workpermits.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=workpermits")}
                >
                  💼 Work Permits
                </div>
              )}
              {PERMISSIONS.workhistory.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=workhistory")}
                >
                  🏢 Work History
                </div>
              )}
              {PERMISSIONS.emergency.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=emergency")}
                >
                  🚨 Emergency Contacts
                </div>
              )}
              {PERMISSIONS.vacation.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=vacation")}
                >
                  🏖️ Vacation Forms
                </div>
              )}
              {PERMISSIONS.directdeposit.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=directdeposit")}
                >
                  🏦 Direct Deposit
                </div>
              )}
              {PERMISSIONS.fuelcards.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin?tab=fuelcards")}
                >
                  ⛽ Fuel Card
                </div>
              )}
            </div>
          </div>

          {/* Tools */}
          <div className="menu-item admin-item">
            <div className="admin-toggle">Tools ⏷</div>
            <div className="admin-menu">
              <div className="admin-item" onClick={() => navigate("/reports")}>
                Reports
              </div>
            </div>
          </div>

          {/* Administration */}
          <div className="menu-item admin-item">
            <div className="admin-toggle">Administration ⏷</div>
            <div className="admin-menu">
              {PERMISSIONS.account_setting.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/account-setting")}
                >
                  Account Setting
                </div>
              )}
              {PERMISSIONS.permissions.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/permissions")}
                >
                  Security Centre
                </div>
              )}
              {PERMISSIONS.users.view && (
                <div
                  className="admin-item"
                  onClick={() => navigate("/admin-users")}
                >
                  Users
                </div>
              )}

              <div
                className="admin-item"
                onClick={() => navigate("/admin-groups")}
              >
                Groups
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT: Reapplied Notice (fully right-aligned) */}
        {String(selectedUser?.re_applied) === "1" && (
          <div className="reapplied-notice-global">
            ⚠️ This is an old profile. The user has re-applied.
          </div>
        )}
      </div>
    </div>
  );
};

export default SubNavigationBar;

